// Per-user theming. A theme is applied by setting <html data-theme="…">; "classic" (the default
// look) means no attribute. The choice is mirrored to localStorage so the login page and every
// reload paint the right theme instantly, before the API responds (no flash-of-wrong-theme).
//
// RepIQ design system 1.0 (Salem, 4 Oct 2026) adds Paper (light), Navy (dark) and "RepIQ (auto)".
// Auto is a CHOICE, not a CSS theme: it resolves to paper or navy from the device's
// prefers-color-scheme and keeps following the device while it is selected. storedTheme() and
// the saved preference on the server both keep the choice ("repiq-auto"), never the resolution.

export const THEMES = [
  { id: "classic", name: "Classic", hint: "The default light look" },
  { id: "signal", name: "Signal & Silence", hint: "Dark, ink-deep, teal" },
  { id: "daybook", name: "Daybook", hint: "Warm paper, light" },
  { id: "clarity", name: "Clarity", hint: "Bigger type, high contrast" },
  { id: "repiq-auto", name: "RepIQ (auto)", hint: "Paper by day, Navy at night: follows your device" },
  { id: "paper", name: "RepIQ Paper", hint: "Warm paper, navy ink" },
  { id: "navy", name: "RepIQ Navy", hint: "Dark mode: deep navy, cyan for AI" },
];

const KEY = "repiq_theme";

const mq = typeof window !== "undefined" && window.matchMedia
  ? window.matchMedia("(prefers-color-scheme: dark)") : null;
let autoListener = null;

/** The CSS theme a choice paints: "repiq-auto" → paper | navy; anything else is itself. */
export function resolveTheme(id) {
  if (id === "repiq-auto") return mq && mq.matches ? "navy" : "paper";
  return id;
}

export function applyTheme(id) {
  const root = document.documentElement;
  const css = resolveTheme(id);
  if (css && css !== "classic") root.setAttribute("data-theme", css);
  else root.removeAttribute("data-theme");
  // keep following the device while "auto" is chosen
  if (mq && autoListener) { mq.removeEventListener("change", autoListener); autoListener = null; }
  if (mq && id === "repiq-auto") { autoListener = () => applyTheme("repiq-auto"); mq.addEventListener("change", autoListener); }
  try { localStorage.setItem(KEY, id || "classic"); } catch { /* ignore */ }
}

export function storedTheme() {
  try { return localStorage.getItem(KEY) || "classic"; } catch { return "classic"; }
}

/** True while one of the RepIQ design-system themes is painted (paper or navy). */
export function isRepIQTheme() {
  const t = typeof document !== "undefined" ? document.documentElement.getAttribute("data-theme") : null;
  return t === "paper" || t === "navy";
}
